import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from '@itsrever/design-system'
import { useTranslation } from 'react-i18next'
import {
  generateWebhookSigningSecret,
  getWebhookSigningSecret,
  listWebhookSigningSecrets,
  revokeWebhookSigningSecret
} from '@/api/settings-api'

const WEBHOOK_SECRET_KEY = ['webhookSigningSecret']

function secretKey(ecommerceId?: string) {
  return [...WEBHOOK_SECRET_KEY, ecommerceId]
}

// The audit history: which secret is active, who created it and when, and the ecommerces
// it covers. Never carries a secret value, so it is safe to keep in the query cache.
export function useWebhookSecretHistory(ecommerceId: string, enabled: boolean) {
  const query = useQuery({
    queryKey: secretKey(ecommerceId),
    queryFn: () => listWebhookSigningSecrets(ecommerceId),
    enabled: enabled && !!ecommerceId
  })

  const secrets = query.data?.secrets ?? []

  return {
    secrets,
    activeSecret: secrets.find((secret) => !secret.revoked_at),
    // The secret belongs to the group, so every one of these ecommerces is signed with
    // it. The page has to say so: from a single-store dashboard the scope is invisible.
    group: query.data?.ecommerce_group ?? '',
    groupEcommerceIds: query.data?.group_ecommerce_ids ?? [],
    loadingSecrets: query.isLoading,
    secretsError: query.isError,
    reloadSecrets: query.refetch
  }
}

// Revealing is deliberately not cached: the plaintext is handed to the component that asked
// for it and nothing else, so it cannot outlive the click that requested it.
export function useRevealWebhookSecret(ecommerceId: string) {
  const { t } = useTranslation()

  const mutation = useMutation({
    mutationFn: () => getWebhookSigningSecret(ecommerceId),
    onError: () => toast({ text: t('webhook_secret.reveal_error'), variant: 'error' })
  })

  return {
    revealSecret: mutation.mutateAsync,
    revealedSecret: mutation.data?.secret,
    revealing: mutation.isPending,
    clearRevealed: mutation.reset
  }
}

export function useGenerateWebhookSecret(ecommerceId: string) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()

  const mutation = useMutation({
    mutationFn: () => generateWebhookSigningSecret(ecommerceId),
    onSuccess: () => {
      toast({ text: t('webhook_secret.generated'), variant: 'success' })
      queryClient.invalidateQueries({ queryKey: secretKey(ecommerceId) })
    },
    onError: () => toast({ text: t('webhook_secret.generate_error'), variant: 'error' })
  })

  return {
    generateSecret: mutation.mutateAsync,
    generatedSecret: mutation.data?.secret,
    generating: mutation.isPending
  }
}

export function useRevokeWebhookSecret(ecommerceId: string) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()

  const mutation = useMutation({
    mutationFn: () => revokeWebhookSigningSecret(ecommerceId),
    onSuccess: () => {
      toast({ text: t('webhook_secret.revoked'), variant: 'success' })
      queryClient.invalidateQueries({ queryKey: secretKey(ecommerceId) })
    },
    onError: () => toast({ text: t('webhook_secret.revoke_error'), variant: 'error' })
  })

  return { revokeSecret: mutation.mutateAsync, revoking: mutation.isPending }
}
